import { useEffect, useState } from 'react';
import type { AxiosError } from 'axios';
import { authService } from '@/services/auth/authService';
import {
  devDeliverEmbedToken,
  devIsAuthStalled,
  devSetAuthStall,
  getEmbedToken,
} from '@/services/embed/embedAuth';
import { useEmbedMode } from '@/hooks/embed/useEmbedMode';
import { embedSurface } from '@/utils/embed';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const unauthorized = { response: { status: 401 } } as AxiosError;

const DEBOUNCE_GAP_MS = 120;

export function EmbeddedAuthTriggers() {
  const embedMode = useEmbedMode();
  const [, tick] = useState(0);

  // The budget lives in module scope, so nothing re-renders when it changes.
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(id);
  }, []);

  const { asksSpent, askMax, unavailable, sabotaged } = authService.embedAuthDiagnostics();
  const embedded = embedMode !== 'standalone';

  // A burst is one ask by design, so an exhaust has to outpace the debounce.
  const exhaust = () => {
    for (let i = 0; i <= askMax; i++) {
      setTimeout(() => authService.handleApiAuthError(unauthorized), i * DEBOUNCE_GAP_MS);
    }
  };

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold text-muted-foreground">
        Embedded auth
        <span className="ml-1.5 font-normal opacity-60">host-supplied token</span>
      </h3>
      <p className="text-[11px] text-muted-foreground">
        Reproduces the retry loop: each 401 asks Hazcheck Validate for a fresh token, capped, then
        goes terminal with <span className="font-mono">auth-unavailable</span>. Only a request that
        succeeds clears it.
      </p>
      <p className="text-[11px] text-muted-foreground">
        <b>Reject tokens</b> is the honest one — it corrupts the host&apos;s token so the API really
        does 401, and the host really does answer. The loop is mutual, so the fabricated 401 below
        only exercises this half.
      </p>

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant="secondary" className="font-mono text-[10px]">
          {embedMode}
        </Badge>
        <Badge variant="secondary" className="font-mono text-[10px]">
          {embedSurface()}
        </Badge>
        <Badge variant="secondary" className="font-mono text-[10px]">
          token {getEmbedToken() ? 'held' : 'none'}
        </Badge>
        <Badge
          variant={asksSpent > 0 ? 'warning' : 'secondary'}
          className="font-mono tabular-nums text-[10px]"
        >
          asks {asksSpent}/{askMax}
        </Badge>
        {unavailable && (
          <Badge variant="destructive" className="font-mono text-[10px]">
            auth-unavailable
          </Badge>
        )}
        {sabotaged && (
          <Badge variant="warning" className="font-mono text-[10px]">
            rejecting
          </Badge>
        )}
        {devIsAuthStalled() && (
          <Badge variant="warning" className="font-mono text-[10px]">
            stalling
          </Badge>
        )}
      </div>

      {embedded ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={sabotaged ? 'destructive' : 'outline'}
              onClick={() => {
                authService.devSetEmbedTokenSabotage(!sabotaged);
                tick((n) => n + 1);
              }}
            >
              {sabotaged ? 'Stop rejecting tokens' : 'Reject tokens (real 401s)'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => authService.handleApiAuthError(unauthorized)}
            >
              Fake one 401
            </Button>
            <Button size="sm" variant="outline" onClick={exhaust}>
              Fake four (exhaust)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => devDeliverEmbedToken(`dev-recovered-${Date.now()}`)}
            >
              Fake host token-refresh
            </Button>
            <Button
              size="sm"
              variant={devIsAuthStalled() ? 'destructive' : 'outline'}
              onClick={() => {
                devSetAuthStall(!devIsAuthStalled());
                tick((n) => n + 1);
              }}
            >
              {devIsAuthStalled() ? 'Stop stalling' : 'Stall inbound tokens'}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            <b>Stall</b> arms the cold load: the bridge drops every token the host sends, including
            the one on <span className="font-mono">init</span>. It survives a reload, so arm it,
            then close and reopen the panel in Validate — the reopened frame never establishes a
            session. Turn it off before reloading again or it stays broken.
          </p>
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          Standalone — a 401 here redirects to Keycloak instead, and these controls need embed mode.
          That needs a real frame (the frame check is what stops a top-level tab claiming it) and a
          real host, since the retry loop is mutual. Open Extract from Validate&apos;s Add-DGD panel
          with <span className="font-mono">dev-up.sh</span>. Inside the frame the nav bar is hidden,
          so the bug button bottom-right is the way back to this panel.
        </p>
      )}
    </section>
  );
}
